import { coachingApi } from "./coaching-api";
import { type CoachingRequester, requestContract } from "./coaching-services";
import type {
  CoachingCapstoneSubmissionResponseDto,
  CoachingCapstoneSubmissionsResponseDto,
  CoachingCertificateIssueRequest,
  CoachingCertificateResponseDto,
  CoachingCertificatesResponseDto,
  CoachingClearSeriesRequest,
  CoachingClearSeriesResponseDto,
  CoachingReportEnqueueResponseDto,
  CoachingReportRegenerateRequest,
  CoachingThresholdsResponseDto,
  CoachingThresholdUpsertRequest,
  CoachingThresholdUpsertResponseDto,
  CoachingToolViewDiagnosticsDto
} from "./coaching-readiness-dtos";

type Query = Record<string, string | number | boolean | null | undefined>;

/**
 * Readiness-lane coaching service family — the NOW-LIVE settings / capstone-read /
 * certificate / report-generate backend slices.
 *
 * Layered on the SHARED coaching transport (requestContract) exactly like the Z6
 * family, so live `orgId` injection (?orgId) and the UUID Idempotency-Key
 * discipline are identical to the rest of the facade — NO new transport, NO new
 * auth path (httpOnly cookies + credentials:'include' + /auth/me stay the only
 * auth, via the preserved coachingApi client). Reads return the backend's wire
 * shapes VERBATIM; the typed-honest states (artifactRef null = pending,
 * report:null = pending/queued) are NOT normalized away — surfaces render them
 * honestly. The live + mock variants are built from the same factory (mock mode
 * routes through the same in-memory requester the e2e/contract fixtures use).
 */
export function createCoachingReadinessServices(requester: CoachingRequester = coachingApi) {
  return {
    settings: {
      // FR-SET: org thresholds. Read is view.settings (staff/coach-where-allowed);
      // the upsert is manage.settings (SA/Admin) + a UUID Idempotency-Key.
      thresholds: (query?: Query) =>
        requestContract<CoachingThresholdsResponseDto>(requester, "settings.thresholds.list", { query }),
      upsertThreshold: (body: CoachingThresholdUpsertRequest, idempotencyKey: string) =>
        requestContract<CoachingThresholdUpsertResponseDto>(requester, "settings.thresholds.upsert", {
          body,
          idempotencyKey
        })
    },
    capstone: {
      // F15 READ: list/detail. coaching.reports.view.org + discriminated read
      // scope — a participant sees ONLY their own submission(s) (server-enforced).
      // reportId is a REFERENCE only; the report body is never returned here.
      listForCohort: (cohortId: string, query?: Query) =>
        requestContract<CoachingCapstoneSubmissionsResponseDto>(requester, "capstone.submissions.listForCohort", {
          pathParams: { cohort_id: cohortId },
          query
        }),
      detail: (submissionId: string) =>
        requestContract<CoachingCapstoneSubmissionResponseDto>(requester, "capstone.submissions.detail", {
          pathParams: { submission_id: submissionId }
        })
    },
    certificates: {
      // FR-CERT: list/detail are scope-isolated (participant sees own); issue is
      // SA/Admin-only + a UUID Idempotency-Key. artifactRef may be null (pending).
      list: (query?: Query) =>
        requestContract<CoachingCertificatesResponseDto>(requester, "certificates.list", { query }),
      detail: (certificateId: string) =>
        requestContract<CoachingCertificateResponseDto>(requester, "certificates.detail", {
          pathParams: { certificate_id: certificateId }
        }),
      issue: (body: CoachingCertificateIssueRequest, idempotencyKey: string) =>
        requestContract<CoachingCertificateResponseDto>(requester, "certificates.issue", { body, idempotencyKey })
    },
    reports: {
      // FR-REPORT: generate (SA/Admin/coach-in-scope) + regenerate (SA only). Both
      // require a UUID Idempotency-Key; the response is the honest enqueue envelope
      // (report:null + status pending/queued — NEVER a fabricated 'generated').
      generate: (sessionId: string, idempotencyKey: string) =>
        requestContract<CoachingReportEnqueueResponseDto>(requester, "reports.generate", {
          pathParams: { session_id: sessionId },
          idempotencyKey
        }),
      regenerate: (sessionId: string, body: CoachingReportRegenerateRequest, idempotencyKey: string) =>
        requestContract<CoachingReportEnqueueResponseDto>(requester, "reports.regenerate", {
          pathParams: { session_id: sessionId },
          body,
          idempotencyKey
        })
    },
    sessions: {
      // FR-SESS-002 Clear Series ("KEEP + DETACH"): SA + coach-in-scope. Requires
      // a destructive { confirm: true } body + a UUID Idempotency-Key. The response
      // is the honest cleared/detached envelope (counts only; …Deleted always
      // false). A foreign/unknown cohort 404s-via-400 (the route maps it).
      clearSeries: (cohortId: string, body: CoachingClearSeriesRequest, idempotencyKey: string) =>
        requestContract<CoachingClearSeriesResponseDto>(requester, "sessions.clearSeries", {
          pathParams: { cohort_id: cohortId },
          body,
          idempotencyKey
        })
    },
    tools: {
      // FR-TOOL-001 Tool View: SUPER-ADMIN ONLY, READ-ONLY. The response carries
      // NO secret values (gate states + counts + configured/not-configured booleans
      // only; secretsRedacted:true). orgId is injected by the shared transport.
      diagnostics: (query?: Query) =>
        requestContract<CoachingToolViewDiagnosticsDto>(requester, "tools.diagnostics", { query })
    }
  };
}

export type CoachingReadinessServices = ReturnType<typeof createCoachingReadinessServices>;
export const coachingReadinessServices = createCoachingReadinessServices();
